import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { ActionButtons } from '../Shared/ActionButtons';
import Loading from '../Shared/ResourceIndex/Loading';
import ToggleSmall from '../Shared/Toggle/ToggleSmall';
import { RolesContext } from '../../contexts/RolesContext';
import billingAddonApi, { BillingAddon, AddonImpactRow } from '../../utils/api/billingAddon';
import BillingAddonModal from './BillingAddonModal';

type FilterStatus = 'all' | 'active' | 'inactive';

interface ImpactModalState {
  addon: BillingAddon;
  impact: AddonImpactRow[];
  action: 'delete' | 'inactivate';
}

const StatusBadge: React.FC<{ status: 'active' | 'inactive' }> = ({ status }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: status === 'active' ? '#1D9E75' : '#888',
      color: '#fff',
    }}
  >
    {status === 'active' ? 'Active' : 'Inactive'}
  </span>
);

const BillingAddons: React.FC = () => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const canManage = permCheck('create', 'Billing Add-ons');

  const [collapse, setCollapse] = useState(true);
  const [addons, setAddons] = useState<BillingAddon[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editAddon, setEditAddon] = useState<BillingAddon | null>(null);
  const [impactModal, setImpactModal] = useState<ImpactModalState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BillingAddon | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await billingAddonApi.index();
      setAddons(data);
    } catch {
      toast.error('Failed to load billing add-ons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!collapse) load();
  }, [collapse]);

  if (!canManage) return null;

  const filtered = addons.filter(a => filter === 'all' || a.status === filter);

  const handleEdit = (addon: BillingAddon) => {
    setEditAddon(addon);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditAddon(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditAddon(null);
  };

  const handleSaved = () => {
    handleModalClose();
    load();
  };

  const handleToggleStatus = async (addon: BillingAddon) => {
    const newStatus = addon.status === 'active' ? 'inactive' : 'active';
    if (newStatus === 'inactive') {
      const impact = await billingAddonApi.impact(addon.id);
      if (impact.length > 0) {
        setImpactModal({ addon, impact, action: 'inactivate' });
        return;
      }
    }
    await doSetStatus(addon, newStatus);
  };

  const doSetStatus = async (addon: BillingAddon, status: 'active' | 'inactive') => {
    setBusy(true);
    try {
      await billingAddonApi.setStatus(addon.id, status);
      toast.success(`Add-on marked as ${status}`);
      load();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setBusy(false);
      setImpactModal(null);
    }
  };

  const handleDelete = async (addon: BillingAddon) => {
    handleModalClose();
    const impact = await billingAddonApi.impact(addon.id);
    if (impact.length > 0) {
      setImpactModal({ addon, impact, action: 'delete' });
    } else {
      setConfirmDelete(addon);
    }
  };

  const doDelete = async (addon: BillingAddon) => {
    setBusy(true);
    try {
      await billingAddonApi.destroy(addon.id);
      toast.success('Add-on deleted');
      load();
    } catch {
      toast.error('Failed to delete add-on');
    } finally {
      setBusy(false);
      setImpactModal(null);
      setConfirmDelete(null);
    }
  };

  const renderDivisions = (addon: BillingAddon) => {
    const divs = addon.divisions || [];
    if (!divs.length) return <span style={{ color: '#999' }}>—</span>;
    if (divs.length <= 3) return divs.map(d => d.name).join(', ');
    return (
      <span title={divs.map(d => d.name).join(', ')}>
        {divs.slice(0, 3).map(d => d.name).join(', ')}{' '}
        <span style={{ background: '#ddd', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
          +{divs.length - 3}
        </span>
      </span>
    );
  };

  return (
    <div className="inspection-intervals">
      <div className="inspection-intervals__header" onClick={() => setCollapse(!collapse)}>
        <h3>Billing Add-ons</h3>
        <span>
          <i className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'} aria-hidden="true" />
        </span>
      </div>
      <hr />
      <br />

      <div className={collapse ? 'hidden inspection-intervals__content' : 'visible inspection-intervals__content'}>
        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(['all', 'active', 'inactive'] as FilterStatus[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid #ccc',
                background: filter === f ? '#1D9E75' : '#fff',
                color: filter === f ? '#fff' : '#333',
                fontWeight: filter === f ? 600 : 400,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Table header */}
        <div className="inspection-intervals__table-head">
          <h3>Name</h3>
          <h3>Code</h3>
          <h3>Status</h3>
          <h3>Assigned Divisions</h3>
          <h3>Include</h3>
        </div>

        {/* Table rows */}
        {loading ? (
          <Loading what="data" />
        ) : filtered.length === 0 ? (
          <p style={{ color: '#999', fontSize: 13, marginTop: 12 }}>No add-ons found.</p>
        ) : (
          filtered.map(addon => (
            <div key={addon.id} className="inspection-intervals__table-body">
              <h3>{addon.name}</h3>
              <h3>{addon.code}</h3>
              <h3><StatusBadge status={addon.status} /></h3>
              <h3>{renderDivisions(addon)}</h3>
              <h3 className="inspection-intervals__include">
                <div>
                  <span className="inspection-intervals__include-toggle">
                    <ToggleSmall
                      isChecked={addon.status === 'active'}
                      handleChange={() => !busy && handleToggleStatus(addon)}
                    />
                  </span>
                  <span className="inspection-intervals__include-edit" onClick={() => handleEdit(addon)}>
                    <FontAwesomeIcon icon={faEdit} title="Edit" />
                  </span>
                </div>
              </h3>
            </div>
          ))
        )}

        <ActionButtons
          className="inspection-intervals__create-new-button"
          primary="Create New Add-on +"
          onClick={handleAddNew}
        />

        <br /><br /><br />
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <BillingAddonModal
          addon={editAddon}
          onClose={handleModalClose}
          onSaved={handleSaved}
          onDelete={editAddon ? () => handleDelete(editAddon) : undefined}
          onImpactDetected={(addon, impact) => {
            handleModalClose();
            setImpactModal({ addon, impact, action: 'inactivate' });
          }}
        />
      )}

      {/* Simple confirm delete (no active form assignments) */}
      {confirmDelete && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={{ marginTop: 0 }}>Delete Add-on</h3>
            <p>Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={() => doDelete(confirmDelete)} disabled={busy} style={deleteBtnStyle}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Impact modal (has active form assignments) */}
      {impactModal && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={{ marginTop: 0 }}>This add-on is currently in use</h3>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
              <strong>{impactModal.addon.name}</strong> is assigned to forms in the following divisions:
            </p>
            <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: 13 }}>
              {impactModal.impact.map(row => (
                <li key={row.divisionId}>
                  {row.divisionName} — {row.projectCount} project{row.projectCount !== 1 ? 's' : ''}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setImpactModal(null)} style={cancelBtnStyle}>Cancel</button>
              {impactModal.action === 'delete' ? (
                <>
                  <button
                    onClick={() => doSetStatus(impactModal.addon, 'inactive')}
                    disabled={busy}
                    style={{ ...cancelBtnStyle, borderColor: '#BA7517', color: '#633806' }}
                  >
                    Mark Inactive
                  </button>
                  <button onClick={() => doDelete(impactModal.addon)} disabled={busy} style={deleteBtnStyle}>
                    Delete
                  </button>
                </>
              ) : (
                <button
                  onClick={() => doSetStatus(impactModal.addon, 'inactive')}
                  disabled={busy}
                  style={{ ...cancelBtnStyle, borderColor: '#BA7517', color: '#633806' }}
                >
                  Mark Inactive
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const dialogStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 6, padding: 24, width: 440, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
const cancelBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #ccc', padding: '6px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const deleteBtnStyle: React.CSSProperties = {
  background: '#c0392b', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};

export default BillingAddons;
